'use client';

import clsx from 'clsx';
import { FC, ReactNode, PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';

import styles from './styles.module.css';

interface Props {
  children: ReactNode;
  className?: string;
}

const THUMB_HEIGHT = 80;
const TRACK_INSET = 16;

const ScrollFadeWrapper: FC<Props> = ({ children, className }) => {
  const [hasScroll, setHasScroll] = useState(false);
  const [thumb, setThumb] = useState<{ height: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startScrollTop: number; trackHeight: number; thumbHeight: number } | null>(
    null
  );

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollableHeight = el.scrollHeight - el.clientHeight;
    const isScrollable = scrollableHeight > 1;

    // Проверяем, есть ли скролл и не дошли ли мы до самого низа (запас 2px)
    const canScrollDown = isScrollable && el.scrollTop + el.clientHeight < el.scrollHeight - 2;
    setHasScroll(canScrollDown);

    if (!isScrollable) {
      setThumb(null);
      return;
    }

    const trackHeight = Math.max(el.clientHeight - TRACK_INSET * 2, 0);
    const thumbHeight = Math.min(THUMB_HEIGHT, trackHeight);
    const progress = el.scrollTop / scrollableHeight;
    const top = TRACK_INSET + progress * (trackHeight - thumbHeight);

    setThumb({ height: thumbHeight, top });
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [children]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const el = scrollRef.current;
      const drag = dragRef.current;
      if (!el || !drag) return;

      const scrollableHeight = el.scrollHeight - el.clientHeight;
      const trackRange = drag.trackHeight - drag.thumbHeight;
      const deltaY = e.clientY - drag.startY;
      const scrollDelta = trackRange > 0 ? (deltaY / trackRange) * scrollableHeight : 0;

      el.scrollTop = Math.min(Math.max(drag.startScrollTop + scrollDelta, 0), scrollableHeight);
      checkScroll();
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      dragRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  const handleThumbPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !thumb) return;

    e.preventDefault();

    dragRef.current = {
      startY: e.clientY,
      startScrollTop: el.scrollTop,
      trackHeight: Math.max(el.clientHeight - TRACK_INSET * 2, 0),
      thumbHeight: thumb.height,
    };
    setIsDragging(true);
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={clsx(styles.scrollContainer, hasScroll && styles.hasScroll, className)}
        ref={scrollRef}
        onScroll={checkScroll}
      >
        {children}
      </div>

      {thumb && (
        <div
          className={clsx(styles.thumb, isDragging && styles.thumbDragging)}
          style={{ height: thumb.height, transform: `translateY(${thumb.top}px)` }}
          onPointerDown={handleThumbPointerDown}
        />
      )}
    </div>
  );
};

export default ScrollFadeWrapper;
